import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  AnimatePresence,
  motion
} from 'motion/react';

import {
  AppWindow,
  ChevronRight,
  HardDrive,
  Lock,
  LogOut,
  Monitor,
  Moon,
  Play,
  Power,
  RefreshCw,
  Search,
  Square,
  Star,
  Wifi,
  X,
  Zap
} from 'lucide-react';

import {
  useRealtime
} from '../../contexts/RealtimeContext';

import {
  kaizenDialogTransition,
  kaizenNavTransition,
  kaizenOverlayTransition,
  kaizenSheetTransition,
  kaizenStateTransition
} from '../../lib/kaizenMotion';


type PcApp = {
  id: string;
  name: string;
  appId: string;
};


type RunningApp = {
  pid: number;
  processName: string;
  title: string;
};


type Favorite = {
  kind: 'app' | 'action';
  targetId: string;
  label: string;
  position: number;
};


type PcResult = {
  success: boolean;
  action: string;
  data?: any;
  error?: string;
};


type PendingConfirmation = {
  action: string;
  title: string;
  description: string;
  pid?: number;
};


interface Props {
  isOpen: boolean;
  onClose: () => void;
}


const ACTION_LABELS:
  Record<string, string> =
{
  'power.lock':
    'Lock PC',

  'power.monitorOff':
    'Monitor Off',

  'power.monitorOn':
    'Monitor On',

  'system.showDesktop':
    'Show Desktop',

  'system.restartExplorer':
    'Restart Explorer',

  'power.sleep':
    'Sleep',

  'power.hibernate':
    'Hibernate',

  'power.signout':
    'Sign Out',

  'power.restart':
    'Restart PC',

  'power.shutdown':
    'Shut Down',

  'power.abortShutdown':
    'Cancel Shutdown'
};


const DESTRUCTIVE_ACTIONS =
  new Set(
    [
      'power.sleep',
      'power.hibernate',
      'power.signout',
      'power.restart',
      'power.shutdown',
      'apps.forceClose'
    ]
  );


export const PCControlModal:
  React.FC<Props> =
({
  isOpen,
  onClose
}) => {

  const {
    device,
    deviceStatus,
    sendJob,
    activeJobLogs
  } =
    useRealtime();


  const [
    activeTab,
    setActiveTab
  ] =
    useState<
      'favorites' |
      'apps' |
      'running' |
      'power' |
      'system'
    >(
      'favorites'
    );


  const [
    apps,
    setApps
  ] =
    useState<
      PcApp[]
    >(
      []
    );


  const [
    running,
    setRunning
  ] =
    useState<
      RunningApp[]
    >(
      []
    );


  const [
    favorites,
    setFavorites
  ] =
    useState<
      Favorite[]
    >(
      []
    );


  const [
    query,
    setQuery
  ] =
    useState(
      ''
    );


  const [
    loading,
    setLoading
  ] =
    useState(
      false
    );


  const [
    statusMessage,
    setStatusMessage
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const [
    pendingConfirmation,
    setPendingConfirmation
  ] =
    useState<
      PendingConfirmation |
      null
    >(
      null
    );


  const lastResultRef =
    useRef<
      string
    >(
      ''
    );


  const online =
    deviceStatus ===
      'online';


  const workingDirectory =
    device?.allowedRoots?.[0] ||
    'F:\\KAIZEN REMOTE';


  const runPcAction =
    async (
      action:
        string,
      options?:
        {
          targetId?:
            string;

          pid?:
            number;

          params?:
            Record<
              string,
              unknown
            >;
        }
    ) => {

      if (
        !device?.id
      ) {

        setStatusMessage(
          'No connected KAIZEN device.'
        );

        return;
      }


      setLoading(
        true
      );


      setStatusMessage(
        ACTION_LABELS[
          action
        ] ||
        'Working...'
      );


      try {

        await sendJob(
          {
            deviceId:
              device.id,

            type:
              'pc_control',

            payload:
              {
                pcAction:
                  action,

                pcTargetId:
                  options?.targetId,

                pcPid:
                  options?.pid,

                pcParams:
                  options?.params
              },

            workingDirectory
          }
        );

      }
      catch (
        error
      ) {

        setLoading(
          false
        );


        setStatusMessage(
          error instanceof Error
            ? error.message
            : 'PC Control request failed.'
        );
      }
    };


  const refreshBootstrap =
    async () => {

      await runPcAction(
        'pc.bootstrap'
      );
    };


  useEffect(
    () => {

      if (
        isOpen &&
        online
      ) {

        void refreshBootstrap();
      }

    },
    [
      isOpen,
      online,
      device?.id
    ]
  );


  useEffect(
    () => {

      const text =
        (
          Array.isArray(
            activeJobLogs
          )
            ? activeJobLogs
            : []
        )
          .map(
            (entry: any) =>
              String(
                entry?.data ??
                entry?.message ??
                entry?.text ??
                ''
              )
          )
          .join(
            '\n'
          );


      const marker =
        'KAIZEN_PC_CONTROL_RESULT::';


      const markerIndex =
        text.lastIndexOf(
          marker
        );


      if (
        markerIndex < 0
      ) {

        return;
      }


      const after =
        text.substring(
          markerIndex +
          marker.length
        );


      const line =
        after
          .split(
            /\r?\n/
          )[0]
          ?.trim();


      if (
        !line ||
        line ===
          lastResultRef.current
      ) {

        return;
      }


      lastResultRef.current =
        line;


      try {

        const result =
          JSON.parse(
            line
          ) as PcResult;


        setLoading(
          false
        );


        if (
          !result.success
        ) {

          setStatusMessage(
            result.error ||
            'PC Control action failed.'
          );

          return;
        }


        if (
          result.action ===
          'pc.bootstrap'
        ) {

          setApps(
            Array.isArray(
              result.data?.apps
            )
              ? result.data.apps
              : []
          );


          setRunning(
            Array.isArray(
              result.data?.running
            )
              ? result.data.running
              : []
          );


          setFavorites(
            Array.isArray(
              result.data?.favorites
            )
              ? result.data.favorites
              : []
          );


          setStatusMessage(
            'PC Control ready'
          );


          return;
        }


        if (
          result.action ===
            'apps.catalog'
        ) {

          setApps(
            Array.isArray(
              result.data
            )
              ? result.data
              : []
          );
        }


        if (
          result.action ===
            'apps.running'
        ) {

          setRunning(
            Array.isArray(
              result.data
            )
              ? result.data
              : []
          );
        }


        if (
          result.action ===
            'favorites.add' ||
          result.action ===
            'favorites.remove' ||
          result.action ===
            'favorites.list'
        ) {

          setFavorites(
            Array.isArray(
              result.data
            )
              ? result.data
              : []
          );
        }


        if (
          result.action ===
            'apps.launch'
        ) {

          setStatusMessage(
            `Opened ${
              result.data?.name ||
              'application'
            }`
          );
        }
        else {

          setStatusMessage(
            ACTION_LABELS[
              result.action
            ] ||
            'Completed'
          );
        }

      }
      catch {

        /*
         * Ignore unrelated console output.
         */
      }

    },
    [
      activeJobLogs
    ]
  );


  const filteredApps =
    useMemo(
      () => {

        const normalized =
          query
            .trim()
            .toLowerCase();


        if (
          !normalized
        ) {

          return apps;
        }


        return apps.filter(
          app =>
            app.name
              .toLowerCase()
              .includes(
                normalized
              )
        );

      },
      [
        apps,
        query
      ]
    );


  const primaryFavorites =
    favorites.slice(
      0,
      10
    );


  const isFavorite =
    (
      targetId:
        string
    ) =>
      favorites.some(
        favorite =>
          favorite.targetId ===
          targetId
      );


  const addFavorite =
    (
      kind:
        'app' |
        'action',
      targetId:
        string,
      label:
        string
    ) => {

      void runPcAction(
        'favorites.add',
        {
          params:
            {
              kind,
              targetId,
              label
            }
        }
      );
    };


  const removeFavorite =
    (
      targetId:
        string
    ) => {

      void runPcAction(
        'favorites.remove',
        {
          targetId
        }
      );
    };


  const requestAction =
    (
      action:
        string,
      title:
        string,
      description:
        string,
      pid?:
        number
    ) => {

      if (
        DESTRUCTIVE_ACTIONS.has(
          action
        )
      ) {

        setPendingConfirmation(
          {
            action,
            title,
            description,
            pid
          }
        );

        return;
      }


      void runPcAction(
        action,
        {
          pid
        }
      );
    };


  const executeFavorite =
    (
      favorite:
        Favorite
    ) => {

      if (
        favorite.kind ===
          'app'
      ) {

        void runPcAction(
          'apps.launch',
          {
            targetId:
              favorite.targetId
          }
        );

        return;
      }


      requestAction(
        favorite.targetId,
        favorite.label,
        `Run ${favorite.label}?`
      );
    };


  const tabs =
    [
      [
        'favorites',
        'Favourites'
      ],

      [
        'apps',
        'Apps'
      ],

      [
        'running',
        'Running'
      ],

      [
        'power',
        'Power'
      ],

      [
        'system',
        'System'
      ]
    ] as const;


  const utilityActions =
    [
      {
        id:
          'power.lock',

        label:
          'Lock PC',

        description:
          'Lock the Windows session',

        icon:
          Lock
      },

      {
        id:
          'power.monitorOff',

        label:
          'Monitor Off',

        description:
          'Turn displays off, keep KAIZEN running',

        icon:
          Monitor
      },

      {
        id:
          'power.monitorOn',

        label:
          'Monitor On',

        description:
          'Wake displays and restore normal sleep behavior',

        icon:
          Monitor
      },
      {
        id:
          'power.sleep',

        label:
          'Sleep',

        description:
          'Put the computer to sleep',

        icon:
          Moon
      },

      {
        id:
          'power.hibernate',

        label:
          'Hibernate',

        description:
          'Hibernate the computer',

        icon:
          Moon
      },

      {
        id:
          'power.signout',

        label:
          'Sign Out',

        description:
          'Sign out of Windows',

        icon:
          LogOut
      },

      {
        id:
          'power.restart',

        label:
          'Restart PC',

        description:
          'Restart after a five-second grace period',

        icon:
          RefreshCw
      },

      {
        id:
          'power.shutdown',

        label:
          'Shut Down',

        description:
          'Power off after a five-second grace period',

        icon:
          Power
      },

      {
        id:
          'power.abortShutdown',

        label:
          'Cancel Shutdown',

        description:
          'Abort a pending Windows shutdown/restart',

        icon:
          X
      }
    ];


  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="
            fixed inset-0
            z-[100]
            flex
            items-end sm:items-center
            justify-center
            bg-slate-950/70
            backdrop-blur-xl
            p-0 sm:p-5
          "
      onMouseDown={
        event => {

          if (
            event.target ===
            event.currentTarget
          ) {

            onClose();
          }
        }
      }
    >

      <motion.div
        initial={{
          opacity: 0,
          y: 16,
          scale: 0.995
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }}
        exit={{
          opacity: 0,
          y: 12,
          scale: 0.996
        }}
        transition={kaizenSheetTransition}
        className="
          relative
          w-full
          sm:max-w-6xl
          h-[94vh]
          sm:h-[88vh]
          overflow-hidden
          rounded-t-[24px]
          sm:rounded-[24px]
          border
          border-white/10
          bg-slate-950/92
          shadow-[0_40px_140px_rgba(0,0,0,0.72)]
          backdrop-blur-3xl
          flex
          flex-col
        "
      >

        <div
          className="
            absolute inset-x-0 top-0
            h-40
            pointer-events-none
            bg-gradient-to-b
            from-indigo-500/10
            to-transparent
          "
        />


        <header
          className="
            relative
            px-4 sm:px-6
            pt-5
            pb-4
            border-b
            border-white/[0.07]
          "
        >

          <div
            className="
              flex
              items-start
              justify-between
              gap-4
            "
          >

            <div
              className="
                flex
                items-center
                gap-3
              "
            >

              <div
                className="
                  w-10 h-10
                  rounded-xl
                  border
                  border-indigo-400/20
                  bg-indigo-400/10
                  flex
                  items-center
                  justify-center
                "
              >
                <Monitor
                  className="
                    w-5 h-5
                    text-indigo-300
                  "
                />
              </div>


              <div>

                <div
                  className="
                    text-[10px]
                    uppercase
                    tracking-[0.24em]
                    text-indigo-300/80
                    font-semibold
                  "
                >
                  KAIZEN HOST CONTROL
                </div>

                <h2
                  className="
                    mt-1
                    text-xl
                    font-semibold
                    text-white
                  "
                >
                  PC Control
                </h2>

                <div
                  className="
                    mt-1
                    flex
                    items-center
                    gap-2
                    text-xs
                    text-slate-400
                  "
                >

                  <motion.span
                    key={
                      online
                        ? 'host-online-dot'
                        : 'host-offline-dot'
                    }
                    initial={{
                      opacity: 0.55,
                      scale: 0.7
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    className={
                      `
                        w-2 h-2
                        rounded-full
                        ${
                          online
                            ? 'bg-emerald-400'
                            : 'bg-rose-400'
                        }
                      `
                    }
                  />

                  <AnimatePresence
                    initial={false}
                    mode="wait"
                  >
                    <motion.span
                      key={
                        online
                          ? 'host-online'
                          : 'host-unavailable'
                      }
                      initial={{
                        opacity: 0,
                        y: 2
                      }}
                      animate={{
                        opacity: 1,
                        y: 0
                      }}
                      exit={{
                        opacity: 0,
                        y: -2
                      }}
                      transition={
                        kaizenStateTransition
                      }
                    >
                      {online
                        ? 'Host online'
                        : 'Host unavailable'}
                    </motion.span>
                  </AnimatePresence>

                  {device?.name
                    ? ` Â· ${device.name}`
                    : ''}

                </div>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              className="
                w-9 h-9
                rounded-xl
                border
                border-white/10
                bg-white/[0.04]
                text-slate-400
                hover:text-white
                hover:bg-white/[0.08]
                transition
                flex
                items-center
                justify-center
              "
            >
              <X
                className="
                  w-4 h-4
                "
              />
            </button>

          </div>


          <div
            className="
              mt-5
              flex
              gap-1
              overflow-x-auto
              no-scrollbar
            "
          >

            {tabs.map(
              (
                [
                  id,
                  label
                ]
              ) => {

                const selected =
                  activeTab ===
                    id;

                return (

                  <button
                    key={
                      id
                    }
                    type="button"
                    onClick={
                      () =>
                        setActiveTab(
                          id
                        )
                    }
                    className={
                      `
                        relative
                        isolate
                        shrink-0
                        px-3 py-2
                        rounded-lg
                        text-xs
                        font-medium
                        transition-colors
                        ${
                          selected
                            ? 'text-white'
                            : 'text-slate-400 hover:text-slate-200'
                        }
                      `
                    }
                  >

                    {selected && (

                      <motion.span
                        layoutId="kaizen-pc-control-tab-highlight"
                        transition={
                          kaizenNavTransition
                        }
                        className="
                          absolute
                          inset-0
                          -z-10
                          rounded-lg
                          bg-white/[0.10]
                          shadow-[inset_0_0_0_1px_rgba(255,255,255,0.10)]
                          pointer-events-none
                        "
                      />

                    )}

                    <span className="relative z-10">
                      {label}
                    </span>

                  </button>

                );
              }
            )}

          </div>

        </header>


        {/* KAIZEN MOBILE HOST ACTIVITY */}
        <div
          className="
            sm:hidden
            relative
            z-10
            px-4
            py-3
            border-b
            border-white/[0.07]
            bg-slate-950/85
            backdrop-blur-xl
          "
          aria-live="polite"
          aria-atomic="true"
        >

          <div
            className="
              flex
              items-center
              gap-3
              min-w-0
            "
          >

            <div
              className={`
                w-8
                h-8
                shrink-0
                rounded-lg
                border
                flex
                items-center
                justify-center

                ${
                  loading
                    ? `
                        border-indigo-400/25
                        bg-indigo-400/10
                        text-indigo-300
                      `
                    : `
                        border-emerald-400/15
                        bg-emerald-400/[0.07]
                        text-emerald-300
                      `
                }
              `}
            >

              <RefreshCw
                className={`
                  w-3.5
                  h-3.5

                  ${
                    loading
                      ? 'animate-spin'
                      : ''
                  }
                `}
              />

            </div>


            <div
              className="
                flex-1
                min-w-0
              "
            >

              <div
                className="
                  text-[9px]
                  uppercase
                  tracking-[0.16em]
                  text-slate-500
                "
              >
                <AnimatePresence
                  initial={false}
                  mode="wait"
                >
                  <motion.span
                    key={
                      loading
                        ? 'host-activity'
                        : 'pc-control'
                    }
                    initial={{
                      opacity: 0,
                      y: 2
                    }}
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    exit={{
                      opacity: 0,
                      y: -2
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    className="block"
                  >
                    {loading
                      ? 'Host Activity'
                      : 'PC Control'}
                  </motion.span>
                </AnimatePresence>
              </div>


              <div
                className="
                  mt-0.5
                  text-[11px]
                  font-medium
                  text-slate-200
                  truncate
                "
              >
                <AnimatePresence
                  initial={false}
                  mode="wait"
                >
                  <motion.span
                    key={
                      loading
                        ? 'executing-host-activity'
                        : (
                            statusMessage ||
                            'Ready'
                          )
                    }
                    initial={{
                      opacity: 0,
                      y: 2
                    }}
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    exit={{
                      opacity: 0,
                      y: -2
                    }}
                    transition={
                      kaizenStateTransition
                    }
                    className="block"
                  >
                    {loading
                      ? 'Executing authenticated host activity...'
                      : (
                          statusMessage ||
                          'Ready'
                        )}
                  </motion.span>
                </AnimatePresence>
              </div>

            </div>


            <AnimatePresence initial={false}>

              {loading && (

                <motion.div
                  initial={{
                    opacity: 0,
                    scale: 0.94,
                    x: 4
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    x: 0
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.96,
                    x: 3
                  }}
                  transition={
                    kaizenStateTransition
                  }
                  className="
                    shrink-0
                    rounded-md
                    border
                    border-indigo-400/15
                    bg-indigo-400/[0.07]
                    px-2
                    py-1
                    text-[8px]
                    uppercase
                    tracking-[0.12em]
                    text-indigo-300
                  "
                >
                  Working
                </motion.div>

              )}

            </AnimatePresence>

          </div>

        </div>

        <main
          className="
            relative
            flex-1
            overflow-y-auto
            p-4 sm:p-6
          "
        >

          {activeTab ===
            'favorites' && (

            <div
              className="
                space-y-6
              "
            >

              <div>

                <div
                  className="
                    flex
                    items-center
                    justify-between
                  "
                >

                  <div>

                    <h3
                      className="
                        text-sm
                        font-semibold
                        text-white
                      "
                    >
                      Quick Actions
                    </h3>

                    <p
                      className="
                        mt-1
                        text-xs
                        text-slate-500
                      "
                    >
                      First 10 favourites are shown here.
                      Up to 20 are stored on this computer.
                    </p>

                  </div>


                  <button
                    type="button"
                    onClick={
                      () =>
                        void refreshBootstrap()
                    }
                    className="
                      w-9 h-9
                      rounded-xl
                      border
                      border-white/[0.08]
                      bg-white/[0.03]
                      text-slate-400
                      hover:text-white
                      flex
                      items-center
                      justify-center
                    "
                  >
                    <RefreshCw
                      className="
                        w-4 h-4
                      "
                    />
                  </button>

                </div>


                {primaryFavorites.length ===
                  0
                  ? (

                    <div
                      className="
                        mt-4
                        rounded-2xl
                        border
                        border-dashed
                        border-white/10
                        bg-white/[0.025]
                        p-8
                        text-center
                      "
                    >

                      <Star
                        className="
                          w-7 h-7
                          mx-auto
                          text-slate-600
                        "
                      />

                      <div
                        className="
                          mt-3
                          text-sm
                          text-slate-300
                        "
                      >
                        No favourites yet
                      </div>

                      <div
                        className="
                          mt-1
                          text-xs
                          text-slate-500
                        "
                      >
                        Star any app or utility to build
                        your own KAIZEN Start Menu.
                      </div>

                    </div>

                  )
                  : (

                    <div
                      className="
                        mt-4
                        grid
                        grid-cols-2
                        sm:grid-cols-3
                        lg:grid-cols-5
                        gap-2.5
                      "
                    >

                      {primaryFavorites.map(
                        favorite => (

                          <button
                            key={
                              `${favorite.kind}:${favorite.targetId}`
                            }
                            type="button"
                            onClick={
                              () =>
                                executeFavorite(
                                  favorite
                                )
                            }
                            className="
                              group
                              text-left
                              min-h-[104px]
                              rounded-2xl
                              border
                              border-white/[0.08]
                              bg-white/[0.035]
                              hover:bg-white/[0.07]
                              hover:border-indigo-400/20
                              p-3
                              transition
                            "
                          >

                            <div
                              className="
                                flex
                                items-start
                                justify-between
                                gap-2
                              "
                            >

                              <div
                                className="
                                  w-8 h-8
                                  rounded-lg
                                  bg-indigo-400/10
                                  flex
                                  items-center
                                  justify-center
                                "
                              >
                                {favorite.kind ===
                                  'app'
                                  ? (
                                    <AppWindow
                                      className="
                                        w-4 h-4
                                        text-indigo-300
                                      "
                                    />
                                  )
                                  : (
                                    <Zap
                                      className="
                                        w-4 h-4
                                        text-amber-300
                                      "
                                    />
                                  )}
                              </div>


                              <span
                                onClick={
                                  event => {

                                    event.stopPropagation();

                                    removeFavorite(
                                      favorite.targetId
                                    );
                                  }
                                }
                                className="
                                  opacity-70
                                  hover:opacity-100
                                  p-1
                                  text-amber-300
                                "
                              >
                                <Star
                                  className="
                                    w-3.5 h-3.5
                                    fill-current
                                  "
                                />
                              </span>

                            </div>


                            <div
                              className="
                                mt-3
                                text-xs
                                font-medium
                                text-slate-200
                                line-clamp-2
                              "
                            >
                              {favorite.label}
                            </div>

                          </button>

                        )
                      )}

                    </div>

                  )}

              </div>

            </div>

          )}


          {activeTab ===
            'apps' && (

            <div>

              <div
                className="
                  relative
                "
              >

                <Search
                  className="
                    absolute
                    left-3.5 top-1/2
                    -translate-y-1/2
                    w-4 h-4
                    text-slate-500
                  "
                />

                <input
                  value={
                    query
                  }
                  onChange={
                    event =>
                      setQuery(
                        event.target.value
                      )
                  }
                  placeholder="Search installed apps..."
                  className="
                    w-full
                    h-11
                    pl-10 pr-4
                    rounded-xl
                    border
                    border-white/[0.08]
                    bg-white/[0.035]
                    text-sm
                    text-white
                    placeholder:text-slate-600
                    outline-none
                    focus:border-indigo-400/30
                    focus:bg-white/[0.05]
                  "
                />

              </div>


              <div
                className="
                  mt-3
                  flex
                  items-center
                  justify-between
                  text-[11px]
                  text-slate-500
                "
              >

                <span>
                  {filteredApps.length} applications
                </span>

                <span>
                  Dynamic Windows catalogue
                </span>

              </div>


              <div
                className="
                  mt-4
                  grid
                  grid-cols-1
                  sm:grid-cols-2
                  lg:grid-cols-3
                  gap-2
                "
              >

                {filteredApps.map(
                  app => {

                    const favorite =
                      isFavorite(
                        app.id
                      );


                    return (

                      <div
                        key={
                          app.id
                        }
                        className="
                          group
                          flex
                          items-center
                          gap-3
                          rounded-xl
                          border
                          border-white/[0.07]
                          bg-white/[0.025]
                          hover:bg-white/[0.055]
                          p-2.5
                          transition
                        "
                      >

                        <button
                          type="button"
                          onClick={
                            () =>
                              void runPcAction(
                                'apps.launch',
                                {
                                  targetId:
                                    app.id
                                }
                              )
                          }
                          className="
                            flex-1
                            min-w-0
                            flex
                            items-center
                            gap-3
                            text-left
                          "
                        >

                          <div
                            className="
                              shrink-0
                              w-9 h-9
                              rounded-lg
                              bg-indigo-400/10
                              flex
                              items-center
                              justify-center
                            "
                          >
                            <AppWindow
                              className="
                                w-4 h-4
                                text-indigo-300
                              "
                            />
                          </div>


                          <div
                            className="
                              min-w-0
                            "
                          >

                            <div
                              className="
                                truncate
                                text-xs
                                font-medium
                                text-slate-200
                              "
                            >
                              {app.name}
                            </div>

                            <div
                              className="
                                mt-0.5
                                text-[10px]
                                text-slate-600
                              "
                            >
                              Click to launch
                            </div>

                          </div>

                        </button>


                        <button
                          type="button"
                          onClick={
                            () => {

                              if (
                                favorite
                              ) {

                                removeFavorite(
                                  app.id
                                );

                              }
                              else {

                                addFavorite(
                                  'app',
                                  app.id,
                                  app.name
                                );
                              }
                            }
                          }
                          className={
                            `
                              shrink-0
                              w-8 h-8
                              rounded-lg
                              flex
                              items-center
                              justify-center
                              ${
                                favorite
                                  ? 'text-amber-300'
                                  : 'text-slate-600 hover:text-amber-300'
                              }
                            `
                          }
                        >
                          <Star
                            className={
                              `
                                w-4 h-4
                                ${
                                  favorite
                                    ? 'fill-current'
                                    : ''
                                }
                              `
                            }
                          />
                        </button>

                      </div>

                    );
                  }
                )}

              </div>

            </div>

          )}


          {activeTab ===
            'running' && (

            <div>

              <div
                className="
                  flex
                  items-center
                  justify-between
                "
              >

                <div>

                  <h3
                    className="
                      text-sm
                      font-semibold
                      text-white
                    "
                  >
                    Running Apps
                  </h3>

                  <p
                    className="
                      mt-1
                      text-xs
                      text-slate-500
                    "
                  >
                    Applications with visible Windows.
                  </p>

                </div>


                <button
                  type="button"
                  onClick={
                    () =>
                      void runPcAction(
                        'apps.running'
                      )
                  }
                  className="
                    px-3 py-2
                    rounded-lg
                    border
                    border-white/[0.08]
                    bg-white/[0.03]
                    text-xs
                    text-slate-300
                    hover:bg-white/[0.07]
                  "
                >
                  Refresh
                </button>

              </div>


              <div
                className="
                  mt-4
                  space-y-2
                "
              >

                {running.map(
                  app => (

                    <div
                      key={
                        `${app.pid}:${app.title}`
                      }
                      className="
                        rounded-xl
                        border
                        border-white/[0.07]
                        bg-white/[0.025]
                        p-3
                        flex
                        items-center
                        gap-3
                      "
                    >

                      <div
                        className="
                          w-9 h-9
                          rounded-lg
                          bg-white/[0.04]
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <AppWindow
                          className="
                            w-4 h-4
                            text-slate-400
                          "
                        />
                      </div>


                      <div
                        className="
                          flex-1
                          min-w-0
                        "
                      >

                        <div
                          className="
                            text-xs
                            font-medium
                            text-slate-200
                            truncate
                          "
                        >
                          {app.title}
                        </div>

                        <div
                          className="
                            mt-0.5
                            text-[10px]
                            text-slate-600
                          "
                        >
                          {app.processName} Â· PID {app.pid}
                        </div>

                      </div>


                      <button
                        type="button"
                        title="Close gracefully"
                        onClick={
                          () =>
                            void runPcAction(
                              'apps.close',
                              {
                                pid:
                                  app.pid
                              }
                            )
                        }
                        className="
                          w-8 h-8
                          rounded-lg
                          text-slate-500
                          hover:text-amber-300
                          hover:bg-amber-400/10
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <Square
                          className="
                            w-3.5 h-3.5
                          "
                        />
                      </button>


                      <button
                        type="button"
                        title="Force close"
                        onClick={
                          () =>
                            requestAction(
                              'apps.forceClose',
                              `Force close ${app.processName}`,
                              `This immediately terminates PID ${app.pid}. Unsaved work may be lost.`,
                              app.pid
                            )
                        }
                        className="
                          w-8 h-8
                          rounded-lg
                          text-slate-500
                          hover:text-rose-300
                          hover:bg-rose-400/10
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <X
                          className="
                            w-4 h-4
                          "
                        />
                      </button>

                    </div>

                  )
                )}

              </div>

            </div>

          )}


          {activeTab ===
            'power' && (

            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-2.5
              "
            >

              {utilityActions.map(
                item => {

                  const Icon =
                    item.icon;

                  const favorite =
                    isFavorite(
                      item.id
                    );


                  return (

                    <div
                      key={
                        item.id
                      }
                      className="
                        rounded-2xl
                        border
                        border-white/[0.07]
                        bg-white/[0.025]
                        p-3
                        flex
                        items-center
                        gap-3
                      "
                    >

                      <button
                        type="button"
                        onClick={
                          () =>
                            requestAction(
                              item.id,
                              item.label,
                              item.description
                            )
                        }
                        className="
                          flex-1
                          min-w-0
                          flex
                          items-center
                          gap-3
                          text-left
                        "
                      >

                        <div
                          className="
                            w-10 h-10
                            rounded-xl
                            bg-indigo-400/10
                            flex
                            items-center
                            justify-center
                          "
                        >
                          <Icon
                            className="
                              w-4 h-4
                              text-indigo-300
                            "
                          />
                        </div>


                        <div>

                          <div
                            className="
                              text-xs
                              font-medium
                              text-slate-200
                            "
                          >
                            {item.label}
                          </div>

                          <div
                            className="
                              mt-1
                              text-[10px]
                              text-slate-600
                            "
                          >
                            {item.description}
                          </div>

                        </div>

                      </button>


                      <button
                        type="button"
                        onClick={
                          () => {

                            if (
                              favorite
                            ) {

                              removeFavorite(
                                item.id
                              );

                            }
                            else {

                              addFavorite(
                                'action',
                                item.id,
                                item.label
                              );
                            }
                          }
                        }
                        className={
                          `
                            w-8 h-8
                            rounded-lg
                            flex
                            items-center
                            justify-center
                            ${
                              favorite
                                ? 'text-amber-300'
                                : 'text-slate-600 hover:text-amber-300'
                            }
                          `
                        }
                      >
                        <Star
                          className={
                            `
                              w-4 h-4
                              ${
                                favorite
                                  ? 'fill-current'
                                  : ''
                              }
                            `
                          }
                        />
                      </button>

                    </div>

                  );
                }
              )}

            </div>

          )}


          {activeTab ===
            'system' && (

            <div
              className="
                grid
                grid-cols-1
                sm:grid-cols-2
                gap-3
              "
            >

              {[
                {
                  action:
                    'system.showDesktop',

                  label:
                    'Show Desktop',

                  description:
                    'Minimize application windows',

                  icon:
                    Monitor
                },

                {
                  action:
                    'system.restartExplorer',

                  label:
                    'Restart Explorer',

                  description:
                    'Restart Windows Explorer shell',

                  icon:
                    RefreshCw
                },

                {
                  action:
                    'system.diskUsage',

                  label:
                    'Disk Status',

                  description:
                    'Query disk capacity and free space',

                  icon:
                    HardDrive
                },

                {
                  action:
                    'system.networkStatus',

                  label:
                    'Network Status',

                  description:
                    'Query adapters and IPv4 addresses',

                  icon:
                    Wifi
                }
              ].map(
                item => {

                  const Icon =
                    item.icon;


                  return (

                    <button
                      key={
                        item.action
                      }
                      type="button"
                      onClick={
                        () =>
                          requestAction(
                            item.action,
                            item.label,
                            item.description
                          )
                      }
                      className="
                        rounded-2xl
                        border
                        border-white/[0.07]
                        bg-white/[0.025]
                        hover:bg-white/[0.06]
                        p-4
                        text-left
                        transition
                        flex
                        items-center
                        gap-3
                      "
                    >

                      <div
                        className="
                          w-10 h-10
                          rounded-xl
                          bg-white/[0.05]
                          flex
                          items-center
                          justify-center
                        "
                      >
                        <Icon
                          className="
                            w-4 h-4
                            text-slate-300
                          "
                        />
                      </div>


                      <div
                        className="
                          flex-1
                        "
                      >

                        <div
                          className="
                            text-xs
                            font-medium
                            text-slate-200
                          "
                        >
                          {item.label}
                        </div>

                        <div
                          className="
                            mt-1
                            text-[10px]
                            text-slate-600
                          "
                        >
                          {item.description}
                        </div>

                      </div>


                      <ChevronRight
                        className="
                          w-4 h-4
                          text-slate-700
                        "
                      />

                    </button>

                  );
                }
              )}

            </div>

          )}

        </main>


        <footer
          className="
            px-4 sm:px-6
            py-3
            border-t
            border-white/[0.07]
            bg-black/10
            flex
            items-center
            justify-between
            gap-3
          "
        >

          <div
            className="
              min-w-0
              text-[10px]
              text-slate-500
              truncate
            "
          >
            <AnimatePresence
              initial={false}
              mode="wait"
            >
              <motion.span
                key={
                  loading
                    ? 'executing-host-action'
                    : (
                        statusMessage ||
                        'Ready'
                      )
                }
                initial={{
                  opacity: 0,
                  y: 2
                }}
                animate={{
                  opacity: 1,
                  y: 0
                }}
                exit={{
                  opacity: 0,
                  y: -2
                }}
                transition={
                  kaizenStateTransition
                }
                className="block"
              >
                {loading
                  ? 'Executing authenticated host action...'
                  : (
                      statusMessage ||
                      'Ready'
                    )}
              </motion.span>
            </AnimatePresence>
          </div>


          <div
            className="
              shrink-0
              text-[9px]
              uppercase
              tracking-[0.16em]
              text-slate-700
            "
          >
            THOR PC CONTROL
          </div>

        </footer>


        <AnimatePresence initial={false}>
          {pendingConfirmation && (

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={kaizenOverlayTransition}
            className="
              absolute inset-0
              z-20
              flex
              items-center
              justify-center
              bg-slate-950/78
              backdrop-blur-xl
              p-5
            "
          >

            <motion.div
              initial={{
                opacity: 0,
                y: 8,
                scale: 0.985
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }}
              exit={{
                opacity: 0,
                y: 6,
                scale: 0.99
              }}
              transition={kaizenDialogTransition}
              className="
                w-full
                max-w-md
                rounded-[20px]
                border
                border-rose-400/15
                bg-slate-900/95
                shadow-[0_30px_100px_rgba(0,0,0,0.7)]
                p-5
              "
            >

              <div
                className="
                  w-10 h-10
                  rounded-xl
                  bg-rose-400/10
                  flex
                  items-center
                  justify-center
                "
              >
                <Power
                  className="
                    w-4 h-4
                    text-rose-300
                  "
                />
              </div>


              <h3
                className="
                  mt-4
                  text-base
                  font-semibold
                  text-white
                "
              >
                {pendingConfirmation.title}
              </h3>


              <p
                className="
                  mt-2
                  text-xs
                  leading-5
                  text-slate-400
                "
              >
                {pendingConfirmation.description}
              </p>


              <div
                className="
                  mt-5
                  flex
                  justify-end
                  gap-2
                "
              >

                <button
                  type="button"
                  onClick={
                    () =>
                      setPendingConfirmation(
                        null
                      )
                  }
                  className="
                    px-4 py-2
                    rounded-lg
                    border
                    border-white/[0.08]
                    text-xs
                    text-slate-300
                  "
                >
                  Cancel
                </button>


                <button
                  type="button"
                  onClick={
                    () => {

                      const pending =
                        pendingConfirmation;


                      setPendingConfirmation(
                        null
                      );


                      void runPcAction(
                        pending.action,
                        {
                          pid:
                            pending.pid
                        }
                      );
                    }
                  }
                  className="
                    px-4 py-2
                    rounded-lg
                    border
                    border-rose-400/20
                    bg-rose-500/15
                    text-xs
                    font-medium
                    text-rose-200
                    hover:bg-rose-500/25
                  "
                >
                  Confirm
                </button>

              </div>

            </motion.div>

          </motion.div>

          )}
        </AnimatePresence>

      </motion.div>

        </motion.div>
      )}
    </AnimatePresence>
  );
};